import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Devices whose primary input hovers with a fine pointer get the hover-reveal
 * size strip; everywhere else the strip stays hidden and the theme quick-add
 * plus is the only control. Must match the media query gating the strip's
 * styles in blocks/_product-card-quick-buy.liquid.
 */
// Width joins the pointer gate — see blocks/_product-card-quick-buy.liquid:
// hover-capable phones (paired mouse, AssistiveTouch, the iOS Simulator)
// must still get the sheet at mobile widths.
const HOVER_FINE_QUERY = '(min-width: 750px) and (hover: hover) and (pointer: fine)';

/** Matches the Bloomreach card's quick-buy success window (assets/bloomreach-product-card.js). */
const ADDED_STATE_DURATION_MS = 2000;

/**
 * Adds a selected product-card size through Horizon's product form.
 *
 * The add-to-cart form lives inside a nested product-form-component, so its
 * `ref` attributes register with that component — query the form and variant
 * input directly instead of using this component's refs.
 *
 * @extends Component
 */
export class ProductCardSizeStrip extends Component {
  requiredRefs = [];

  /** @returns {HTMLFormElement | null} */
  get #form() {
    return this.querySelector('form[data-type="add-to-cart-form"]');
  }

  /** @returns {HTMLInputElement | null} */
  get #variantInput() {
    return this.#form?.querySelector('input[name="id"]') ?? null;
  }
  #abortController = new AbortController();

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #addedResetTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.closest('product-card')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('mouseleave', this.#resetAdded, { signal: this.#abortController.signal });

    this.#applySheetContext();
  }

  /**
   * Ports the Bloomreach card's quick-add wiring (assets/bloomreach-product-card.js)
   * to the theme card: on devices without hover the strip never shows, so sized
   * cards reuse the complete-the-look context and the quick-add plus opens the
   * shared size bottom sheet instead of the full quick-add modal. Products
   * without a size option keep the default modal — the only presentation that
   * can express their options. Desktop behaviour is untouched.
   */
  #applySheetContext() {
    if (window.matchMedia(HOVER_FINE_QUERY).matches) return;
    if (!this.querySelector('.product-card-quick-buy__size-button')) return;

    const quickAdd = this.querySelector('quick-add-component');
    if (!(quickAdd instanceof HTMLElement)) return;
    // A one-variant product has nothing to choose. Routing it through the
    // size sheet rendered the whole product page as an undismissable panel,
    // because the sheet needs a size fieldset the variant picker never
    // renders for a single variant (SM-537). Leave it as a direct add.
    if (quickAdd.dataset.quickAddButton === 'add') return;

    quickAdd.dataset.quickAddContext = 'complete-the-look';
    quickAdd.setAttribute('data-quick-add-button', 'choose');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    window.clearTimeout(this.#addedResetTimeout);
  }

  /**
   * Settles the check the size click showed optimistically (#handleSizeClick):
   * a successful add restarts its timer, a failed one reverts to the plus.
   * Adds from the quick-add fallback inside this element go through the
   * same event but their own button already animates.
   *
   * @param {import('@theme/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const form = this.#form;
    if (!form || !(event.target instanceof Node) || !form.parentElement?.contains(event.target)) return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) this.#resetAdded();
        else this.#showAdded();
      })
      .catch(() => this.#resetAdded());
  };

  #showAdded() {
    const plus = this.querySelector('.product-card-quick-buy__plus');
    if (!(plus instanceof HTMLElement)) return;

    plus.dataset.added = 'true';
    if (!plus.dataset.defaultLabel) plus.dataset.defaultLabel = plus.getAttribute('aria-label') ?? '';
    if (plus.dataset.addedLabel) plus.setAttribute('aria-label', plus.dataset.addedLabel);

    window.clearTimeout(this.#addedResetTimeout);
    this.#addedResetTimeout = setTimeout(this.#resetAdded, ADDED_STATE_DURATION_MS);
  }

  #resetAdded = () => {
    window.clearTimeout(this.#addedResetTimeout);

    const plus = this.querySelector('.product-card-quick-buy__plus');
    if (!(plus instanceof HTMLElement) || plus.dataset.added !== 'true') return;

    delete plus.dataset.added;
    if (plus.dataset.defaultLabel !== undefined) {
      plus.setAttribute('aria-label', plus.dataset.defaultLabel);
      delete plus.dataset.defaultLabel;
    }
  };

  /** @param {MouseEvent & {target: HTMLElement}} event */
  handleSizeClick(event) {
    const button = event.target.closest('button[data-variant-id]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const form = this.#form;
    const variantInput = this.#variantInput;
    if (!form || !variantInput) return;

    variantInput.value = variantId;
    // The check shows as soon as the add is committed, not when the response
    // lands — the round trip is long enough to make the click feel ignored.
    this.#showAdded();
    form.requestSubmit();
  }

  /**
   * The "Add"/"Choose" buttons are both always in the DOM (snippets/quick-add.liquid)
   * and toggled purely by CSS keyed off quick-add-component's data-quick-add-button
   * (assets/base.css) — the hidden one stays enabled, so querying `:not([disabled])`
   * alone matches whichever button happens to be submittable, regardless of which
   * one is actually visible. #applySheetContext already set data-quick-add-button
   * to 'choose' for every sized card on this device, so click must follow that
   * same flag instead of re-deriving visibility from disabled state.
   */
  handleQuickAdd() {
    const quickAdd = this.querySelector('quick-add-component');
    const useAddButton = quickAdd?.dataset.quickAddButton === 'add';
    const addButton = useAddButton ? this.querySelector('.quick-add__button--add:not([disabled])') : null;
    const chooseButton = this.querySelector('.quick-add__button--choose');
    (addButton ?? chooseButton)?.click();
  }

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #handleProductSelect = (event) => {
    event.promise?.then(({ detail }) => {
      const nextStrip = detail?.html?.querySelector('product-card-size-strip');
      if (!(nextStrip instanceof HTMLElement)) return;

      const currentButtons = this.querySelectorAll('button[data-variant-id]');
      const nextButtons = nextStrip.querySelectorAll('button[data-variant-id]');

      currentButtons.forEach((button, index) => {
        const nextButton = nextButtons[index];
        if (!(nextButton instanceof HTMLButtonElement)) return;

        button.dataset.variantId = nextButton.dataset.variantId ?? '';
        button.disabled = nextButton.disabled;
        button.setAttribute('aria-disabled', String(nextButton.disabled));
        button.textContent = nextButton.textContent;
        const nextLabel = nextButton.getAttribute('aria-label');
        if (nextLabel) button.setAttribute('aria-label', nextLabel);
      });

      const nextVariantId = nextStrip.querySelector('input[name="id"]');
      const variantInput = this.#variantInput;
      if (nextVariantId instanceof HTMLInputElement && variantInput) variantInput.value = nextVariantId.value;
    });
  };
}

if (!customElements.get('product-card-size-strip')) {
  customElements.define('product-card-size-strip', ProductCardSizeStrip);
}
